import { useMemo, useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Equal endpoints
 * collapse to a single value (e.g. "2 - 2" → "2 Bedroom",
 * "Studio - Studio" → "Studio").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse equal endpoints: "2 - 2" → "2", "Studio - Studio" → "Studio".
  // Compare case-insensitively so "studio - Studio" still collapses cleanly.
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

/**
 * Pinned-parallax tuning.
 *
 * The box keeps its original height — no extra scroll track. The static photo is
 * "pinned" by scroll-locking the image layer inside the box's `overflow:hidden`:
 * the layer is one viewport tall and gets a scroll-linked translate that cancels
 * the box's movement, so the image holds still in the viewport (like a fixed
 * background) while the box — and the title/subtitle inside it — scroll over it.
 * PIN_STRENGTH of 1 = fully pinned; lower values give a softer, slower drift.
 */
const PIN_STRENGTH = 1;

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  // Pinned background without adding any height: the image layer is one
  // viewport tall and translated to cancel the box's own scroll, so it holds
  // still in the viewport (clipped to the box) while the box scrolls over it.
  // Ref on the <section>: it has no top padding, so its viewport top equals the
  // box's top (EditableSlotGroup renders the box but doesn't forward a ref).
  const sectionRef = useRef<HTMLElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = sectionRef.current;
    const layer = layerRef.current;
    if (!box || !layer) return;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      // The layer sits at top:0 of the box. Translating it by -boxTop keeps its
      // top edge at viewport y=0 regardless of scroll, so the (viewport-tall)
      // image appears locked in place while the box slides past.
      const boxTop = box.getBoundingClientRect().top;
      const shift = -boxTop * PIN_STRENGTH;
      layer.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <section
      ref={sectionRef}
      className="hero-boxed relative w-full bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 pt-0 pb-0"
    >
      {/* Boxed static photo at its original height. overflow-hidden clips the
          scroll-locked image layer to the box, which is what produces the
          pinned-background look. */}
      <EditableSlotGroup
        groupPath="hero.bg_image"
        definition={featuredImageDef}
        className="hero-box relative w-full overflow-hidden"
      >
        {/* Scroll-locked image layer — one viewport tall, translated by the
            effect so it holds still while the box scrolls over it. */}
        <div ref={layerRef} className="hero-pinlayer">
          {bgImage.image ? (
            <div className="hero-photo">
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
                style={{ imageRendering: 'high-quality' as any }}
              />
            </div>
          ) : (
            <div className="hero-photo">
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            </div>
          )}
        </div>

        {/* Foreground content. Lives in the box (not the pinned layer), so it
            scrolls with the box over the held-still image. Pointer events are
            disabled on the wrapper and re-enabled only on interactive children
            so empty space passes clicks through to anything floating on top.
            Legibility against the bright photo comes from text-shadow only —
            no dark overlay masks on the image. */}
        <div className="hero-copy absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-white px-6 sm:px-8 pointer-events-none">
          <RichTextContent
            path="hero.title"
            label="Title"
            defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
            as="h1"
            className="hero-title hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-5 max-w-5xl leading-[1.1] tracking-tight pointer-events-auto"
          />

          <RichTextContent
            path="hero.subtitle"
            label="Subtitle"
            defaultValue={subtitleHtml}
            as="p"
            className="hero-subtitle hero-entrance hero-entrance-3 font-body text-lg sm:text-xl md:text-2xl max-w-5xl mb-10 leading-relaxed pointer-events-auto"
          />
        </div>
      </EditableSlotGroup>

      <style>{`
        /* Desktop keeps the original tall box. */
        .hero-box {
          height: clamp(440px, 78vh, 880px);
        }
        .hero-copy {
          transform: translateY(8%);
        }
        /* Strong, clean text-shadow so white type stays legible on the
           unmasked interior photo without darkening the image itself. */
        .hero-title {
          text-shadow:
            0 1px 2px rgba(0, 0, 0, 0.9),
            0 2px 8px rgba(0, 0, 0, 0.65),
            0 6px 24px rgba(0, 0, 0, 0.45);
        }
        .hero-subtitle {
          text-shadow:
            0 1px 2px rgba(0, 0, 0, 0.85),
            0 2px 6px rgba(0, 0, 0, 0.55),
            0 4px 16px rgba(0, 0, 0, 0.4);
        }
        /* Mobile / tablet: shorter than desktop so callouts sit nearer the fold,
           but tall enough that the image and title/subtitle stay readable. */
        @media (max-width: 639px) {
          .hero-box {
            height: clamp(220px, 32svh, 340px);
          }
          .hero-copy {
            transform: none;
            padding-inline: 1.25rem;
          }
          .hero-title {
            margin-bottom: 0.75rem;
            font-size: 2rem;
            line-height: 1.15;
          }
          .hero-subtitle {
            margin-bottom: 0;
            font-size: 1rem;
            line-height: 1.4;
          }
        }
        @media (min-width: 640px) and (max-width: 1023px) {
          .hero-box {
            height: clamp(300px, 48svh, 480px);
          }
          .hero-copy {
            transform: translateY(4%);
          }
          .hero-title {
            margin-bottom: 1rem;
          }
          .hero-subtitle {
            margin-bottom: 0;
          }
        }
        /* Scroll-locked image layer: one viewport tall, anchored to the box top,
           translated by the effect so it holds still while the box scrolls over
           it. The box's overflow:hidden clips it to the visible window. */
        .hero-pinlayer {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          height: 100svh;
          z-index: 0;
          will-change: transform;
        }
        .hero-photo {
          position: absolute;
          inset: 0;
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          /* No parallax: neutralise any stale transform and let the image layer
             fill the box normally (the JS effect is skipped under this
             preference). */
          .hero-pinlayer { height: 100%; transform: none !important; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
